/**
 * StimaCostoRicostruzioneWizard — Fase 10C.1
 *
 * Wizard 6-step riusabile per stima costo ricostruzione.
 * Modalità:
 *  - mode="public": post-submit mostra ringraziamento, NESSUN €
 *  - mode="admin":  post-submit mostra breakdown completo (solo staff)
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2, Building2, Building, Briefcase, Hotel, ShoppingBag, Home, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ComuneAutocomplete from "@/components/common/ComuneAutocomplete";

const API = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = "stima_ricostruzione_draft_v1";

const TIPOLOGIE_GROUPS = [
  { key: "residenziale", label: "Residenziale", Icon: Home, items: [
    { value: "residenziale_monofamiliare", label: "Villa monofamiliare" },
    { value: "residenziale_bifamiliare",   label: "Bifamiliare" },
    { value: "residenziale_schiera",       label: "Casa a schiera" },
    { value: "residenziale_palazzina",     label: "Palazzina/Condominio" },
  ]},
  { key: "industriale", label: "Industriale", Icon: Building2, items: [
    { value: "industriale_capannone_ca",       label: "Capannone CA" },
    { value: "industriale_capannone_acciaio",  label: "Capannone in acciaio" },
    { value: "industriale_capannone_muratura", label: "Capannone in muratura" },
  ]},
  { key: "uffici", label: "Uffici", Icon: Briefcase, items: [
    { value: "ufficio_classe_a", label: "Classe A — direzionale" },
    { value: "ufficio_classe_b", label: "Classe B — standard" },
    { value: "ufficio_classe_c", label: "Classe C — economico" },
  ]},
  { key: "alberghiero", label: "Alberghiero", Icon: Hotel, items: [
    { value: "alberghiero_2_3_stelle", label: "2-3 stelle" },
    { value: "alberghiero_4_stelle",   label: "4 stelle" },
    { value: "alberghiero_5_stelle",   label: "5 stelle" },
  ]},
  { key: "commerciale", label: "Commerciale", Icon: ShoppingBag, items: [
    { value: "commerciale_negozio",                label: "Negozio (<150mq)" },
    { value: "commerciale_medio",                  label: "Punto vendita medio" },
    { value: "commerciale_centro_commerciale",     label: "Centro commerciale" },
  ]},
];

const INITIAL = {
  // Step 1 — località
  geo: { indirizzo: "", comune: "", provincia: "", cap: "", regione: "", lat: null, lng: null },
  // Step 2 — tipologia
  tipologia_group: "",
  tipologia: "",
  // Step 3 — dimensioni
  superficie_mq: "",
  altezza_interpiano_m: 3.0,
  piani_fuori_terra: 1,
  piani_interrati: 0,
  ha_sottotetto_abitabile: false,
  ha_ascensore: false,
  ha_terrazzi: false,
  // Step 4 — tecnologia
  tecnologia_costruttiva: "ca",
  anno_costruzione: 2010,
  // Step 5 — finiture
  livello_finiture: "standard",
  livello_impianti: "standard",
  livello_sicurezza: "standard",
  note_libere: "",
  // Step 6 — contatti
  contatto: { nome: "", cognome: "", email: "", telefono: "", consenso_privacy: false, consenso_marketing: false },
  _hp: "",
};

const STEP_LABELS = ["Localizzazione", "Tipologia", "Dimensioni", "Tecnologia", "Finiture", "Contatti"];

function ageLabel(year) {
  const y = parseInt(year || 0, 10);
  if (!y) return "";
  if (y < 1960) return "Vintage";
  if (y < 1990) return "Datato";
  if (y < 2010) return "Moderno";
  if (y < 2020) return "Recente";
  return "Nuovo";
}

export default function StimaCostoRicostruzioneWizard({ mode = "public", onComplete, onCancel }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(() => {
    if (mode !== "public") return INITIAL;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...INITIAL, ...JSON.parse(raw) };
    } catch (_e) { /* ignore */ }
    return INITIAL;
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [adminResult, setAdminResult] = useState(null);

  // Persist draft (solo public)
  useEffect(() => {
    if (mode !== "public" || submitted) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (_e) { /* ignore */ }
  }, [data, mode, submitted]);

  const upd = (path, val) => {
    setData(prev => {
      const next = { ...prev };
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const stepValid = useMemo(() => {
    switch (step) {
      case 0: return !!(data.geo.comune && data.geo.provincia && data.geo.regione);
      case 1: return !!data.tipologia;
      case 2: return parseFloat(data.superficie_mq) > 0;
      case 3: return !!data.tecnologia_costruttiva;
      case 4: return !!data.livello_finiture;
      case 5: return !!(data.contatto.nome && data.contatto.cognome && data.contatto.email && data.contatto.telefono && data.contatto.consenso_privacy);
      default: return false;
    }
  }, [step, data]);

  const next = () => { if (stepValid) setStep(s => Math.min(STEP_LABELS.length - 1, s + 1)); };
  const prev = () => setStep(s => Math.max(0, s - 1));

  const submit = async () => {
    if (!stepValid) return;
    setSubmitting(true);
    try {
      const payload = {
        contatto: { ...data.contatto },
        input_immobile: {
          tipologia: data.tipologia,
          superficie_mq: parseFloat(data.superficie_mq),
          altezza_interpiano_m: parseFloat(data.altezza_interpiano_m) || 3.0,
          piani_fuori_terra: parseInt(data.piani_fuori_terra, 10) || 0,
          piani_interrati: parseInt(data.piani_interrati, 10) || 0,
          ha_sottotetto_abitabile: !!data.ha_sottotetto_abitabile,
          ha_ascensore: !!data.ha_ascensore,
          ha_terrazzi: !!data.ha_terrazzi,
          tecnologia_costruttiva: data.tecnologia_costruttiva,
          anno_costruzione: parseInt(data.anno_costruzione, 10) || null,
          livello_finiture: data.livello_finiture,
          livello_impianti: data.livello_impianti,
          livello_sicurezza: data.livello_sicurezza,
          note_libere: data.note_libere || "",
        },
        geo: {
          indirizzo: data.geo.indirizzo || "",
          comune: data.geo.comune,
          provincia: data.geo.provincia,
          regione: data.geo.regione,
          cap: data.geo.cap || "",
          lat: data.geo.lat,
          lng: data.geo.lng,
        },
        applica_modulazione_geo: true,
        _hp: data._hp,
      };
      if (mode === "public") {
        const r = await axios.post(`${API}/api/valore-ricostruzione/submit`, payload);
        setSubmitted(true);
        try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
        toast.success("Richiesta inviata");
        if (onComplete) onComplete(r.data);
      } else {
        const r = await axios.post(`${API}/api/admin/valore-ricostruzione/manuale`, payload, { withCredentials: true });
        setAdminResult(r.data);
        setSubmitted(true);
        toast.success("Stima creata");
        if (onComplete) onComplete(r.data);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore invio richiesta";
      toast.error(typeof detail === "string" ? detail : "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  // -- View: post-submit --
  if (submitted && mode === "public") {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-xl mx-auto">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h2 className="font-heading text-2xl text-[#163321] mb-2">Richiesta inviata</h2>
        <p className="text-zinc-600">
          Grazie! Abbiamo ricevuto la tua richiesta. Un consulente Colucci ti contatterà entro 24 ore con la valutazione completa.
        </p>
        <p className="text-xs text-zinc-400 mt-6">
          Verrai contattato all'indirizzo che hai indicato. Conserva il riferimento per la tua richiesta.
        </p>
      </motion.div>
    );
  }

  if (submitted && mode === "admin" && adminResult) {
    return <AdminResultView result={adminResult} onReset={() => { setSubmitted(false); setAdminResult(null); setStep(0); setData(INITIAL); }} />;
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 max-w-3xl mx-auto" data-testid="stima-wizard">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-zinc-500 mb-2">
          {STEP_LABELS.map((l, i) => (
            <span key={l} className={i === step ? "font-bold text-[#163321]" : ""}>
              {i + 1}. {l}
            </span>
          ))}
        </div>
        <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#163321] transition-all" style={{ width: `${((step + 1) / STEP_LABELS.length) * 100}%` }} />
        </div>
      </div>

      {/* Honeypot invisible */}
      <input type="text" name="_hp" value={data._hp} onChange={e => upd("_hp", e.target.value)} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} aria-hidden="true" />

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.18 }}>
          {step === 0 && <StepGeo data={data} upd={upd} />}
          {step === 1 && <StepTipologia data={data} upd={upd} />}
          {step === 2 && <StepDimensioni data={data} upd={upd} />}
          {step === 3 && <StepTecnologia data={data} upd={upd} />}
          {step === 4 && <StepFiniture data={data} upd={upd} />}
          {step === 5 && <StepContatti data={data} upd={upd} mode={mode} />}
        </motion.div>
      </AnimatePresence>

      {/* Footer nav */}
      <div className="flex justify-between items-center mt-8 pt-6 border-t border-zinc-100">
        <button onClick={onCancel || prev} disabled={step === 0 && !onCancel} className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 disabled:opacity-30 inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> {step === 0 ? (onCancel ? "Annulla" : "Indietro") : "Indietro"}
        </button>
        {step < STEP_LABELS.length - 1 ? (
          <button onClick={next} disabled={!stepValid} data-testid="stima-wizard-next" className="px-6 py-2 bg-[#163321] text-white rounded-lg hover:bg-[#163321]/90 disabled:opacity-40 inline-flex items-center gap-1">
            Avanti <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={!stepValid || submitting} data-testid="stima-wizard-submit" className="px-6 py-2 bg-[#163321] text-white rounded-lg hover:bg-[#163321]/90 disabled:opacity-40 inline-flex items-center gap-1">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {mode === "public" ? "Invia richiesta" : "Crea stima"}
          </button>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Step components
// ===========================================================================
function StepGeo({ data, upd }) {
  return (
    <div>
      <h3 className="font-heading text-xl text-[#163321] mb-2">Dove si trova l'immobile?</h3>
      <p className="text-sm text-zinc-500 mb-6">Indicaci comune, provincia e regione. Se vuoi, anche l'indirizzo per una stima più puntuale.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="text-xs text-zinc-600 mb-1 block">Indirizzo (opzionale)</label>
          <input type="text" value={data.geo.indirizzo} onChange={e => upd("geo.indirizzo", e.target.value)} placeholder="Via Roma 10" className="w-full px-3 py-2 border border-zinc-300 rounded-lg" data-testid="stima-indirizzo" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Comune *</label>
          <ComuneAutocomplete value={data.geo.comune} onChange={(v) => upd("geo.comune", v)} onSelect={(item) => { upd("geo.comune", item.name); if (item.province) upd("geo.provincia", item.province); }} placeholder="Es. Napoli" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Provincia (sigla) *</label>
          <input type="text" maxLength={2} value={data.geo.provincia} onChange={e => upd("geo.provincia", e.target.value.toUpperCase())} placeholder="NA" className="w-full px-3 py-2 border border-zinc-300 rounded-lg uppercase" data-testid="stima-provincia" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Regione *</label>
          <input type="text" value={data.geo.regione} onChange={e => upd("geo.regione", e.target.value)} placeholder="Campania" className="w-full px-3 py-2 border border-zinc-300 rounded-lg" data-testid="stima-regione" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">CAP</label>
          <input type="text" maxLength={5} value={data.geo.cap} onChange={e => upd("geo.cap", e.target.value.replace(/\D/g, "").slice(0, 5))} className="w-full px-3 py-2 border border-zinc-300 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function StepTipologia({ data, upd }) {
  return (
    <div>
      <h3 className="font-heading text-xl text-[#163321] mb-2">Che tipo di immobile è?</h3>
      <p className="text-sm text-zinc-500 mb-6">Seleziona la categoria e poi il sottotipo.</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {TIPOLOGIE_GROUPS.map(g => {
          const Icon = g.Icon;
          const active = data.tipologia_group === g.key;
          return (
            <button key={g.key} onClick={() => { upd("tipologia_group", g.key); upd("tipologia", ""); }} className={`p-4 border rounded-xl text-center transition-all ${active ? "border-[#163321] bg-[#163321]/5" : "border-zinc-200 hover:border-zinc-300"}`} data-testid={`stima-group-${g.key}`}>
              <Icon className={`w-7 h-7 mx-auto mb-2 ${active ? "text-[#163321]" : "text-zinc-500"}`} />
              <div className={`text-xs ${active ? "font-bold" : ""}`}>{g.label}</div>
            </button>
          );
        })}
      </div>
      {data.tipologia_group && (
        <div className="grid sm:grid-cols-2 gap-3">
          {TIPOLOGIE_GROUPS.find(g => g.key === data.tipologia_group).items.map(it => (
            <button key={it.value} onClick={() => upd("tipologia", it.value)} className={`p-3 border rounded-lg text-left text-sm transition-all ${data.tipologia === it.value ? "border-[#163321] bg-[#163321] text-white" : "border-zinc-200 hover:border-zinc-300"}`} data-testid={`stima-tipo-${it.value}`}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StepDimensioni({ data, upd }) {
  const stt = data.ha_sottotetto_abitabile ? "abitabile" : "no";
  return (
    <div>
      <h3 className="font-heading text-xl text-[#163321] mb-2">Caratteristiche dimensionali</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Superficie utile (mq) *</label>
          <input type="number" min="1" step="1" value={data.superficie_mq} onChange={e => upd("superficie_mq", e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-lg" data-testid="stima-superficie" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Altezza interpiano (m)</label>
          <input type="number" step="0.1" min="2" max="8" value={data.altezza_interpiano_m} onChange={e => upd("altezza_interpiano_m", e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-lg" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Piani fuori terra</label>
          <input type="number" min="0" max="40" value={data.piani_fuori_terra} onChange={e => upd("piani_fuori_terra", e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-lg" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Piani interrati</label>
          <input type="number" min="0" max="5" value={data.piani_interrati} onChange={e => upd("piani_interrati", e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-lg" />
        </div>
        <div className="sm:col-span-2 grid sm:grid-cols-3 gap-3 mt-2">
          <Checkbox label="Ascensore presente" checked={data.ha_ascensore} onChange={v => upd("ha_ascensore", v)} />
          <Checkbox label="Terrazzi/balconi" checked={data.ha_terrazzi} onChange={v => upd("ha_terrazzi", v)} />
          <Checkbox label="Sottotetto abitabile" checked={data.ha_sottotetto_abitabile} onChange={v => upd("ha_sottotetto_abitabile", v)} />
        </div>
      </div>
    </div>
  );
}

function StepTecnologia({ data, upd }) {
  const techs = [
    { value: "ca",       label: "Cemento armato" },
    { value: "muratura", label: "Muratura portante" },
    { value: "acciaio",  label: "Struttura in acciaio" },
    { value: "legno",    label: "Struttura in legno (X-LAM, ecc.)" },
  ];
  return (
    <div>
      <h3 className="font-heading text-xl text-[#163321] mb-2">Tecnologia costruttiva</h3>
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        {techs.map(t => (
          <button key={t.value} onClick={() => upd("tecnologia_costruttiva", t.value)} className={`p-3 border rounded-lg text-left transition-all ${data.tecnologia_costruttiva === t.value ? "border-[#163321] bg-[#163321] text-white" : "border-zinc-200 hover:border-zinc-300"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div>
        <label className="text-xs text-zinc-600 mb-1 block">
          Anno di costruzione: <strong>{data.anno_costruzione}</strong> <span className="text-emerald-700 ml-2">{ageLabel(data.anno_costruzione)}</span>
        </label>
        <input type="range" min="1900" max="2026" value={data.anno_costruzione} onChange={e => upd("anno_costruzione", parseInt(e.target.value, 10))} className="w-full" />
        <div className="flex justify-between text-xs text-zinc-400 mt-1">
          <span>1900</span><span>1950</span><span>2000</span><span>2026</span>
        </div>
      </div>
    </div>
  );
}

function StepFiniture({ data, upd }) {
  const livs = ["economico", "standard", "pregio"];
  const imps = ["base", "standard", "domotica"];
  const sics = ["base", "standard", "alta"];
  const Card = ({ items, value, onChange, label }) => (
    <div>
      <div className="text-xs text-zinc-600 mb-1">{label}</div>
      <div className="grid grid-cols-3 gap-2">
        {items.map(v => (
          <button key={v} onClick={() => onChange(v)} className={`px-3 py-2 text-xs border rounded-lg capitalize ${value === v ? "bg-[#163321] text-white border-[#163321]" : "border-zinc-200"}`}>
            {v}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-5">
      <h3 className="font-heading text-xl text-[#163321] mb-2">Finiture e impianti</h3>
      <Card items={livs} value={data.livello_finiture} onChange={v => upd("livello_finiture", v)} label="Livello finiture" />
      <Card items={imps} value={data.livello_impianti} onChange={v => upd("livello_impianti", v)} label="Livello impianti" />
      <Card items={sics} value={data.livello_sicurezza} onChange={v => upd("livello_sicurezza", v)} label="Livello sicurezza" />
      <div>
        <label className="text-xs text-zinc-600 mb-1 block">Note libere (opzionale)</label>
        <textarea maxLength={500} rows={3} value={data.note_libere} onChange={e => upd("note_libere", e.target.value)} placeholder="Caratteristiche particolari, vincoli, ristrutturazioni recenti…" className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
        <div className="text-xs text-zinc-400 text-right">{(data.note_libere || "").length}/500</div>
      </div>
    </div>
  );
}

function StepContatti({ data, upd, mode }) {
  return (
    <div>
      <h3 className="font-heading text-xl text-[#163321] mb-2">{mode === "admin" ? "Dati cliente" : "I tuoi dati"}</h3>
      <p className="text-sm text-zinc-500 mb-6">{mode === "admin" ? "Inserisci i dati del cliente per cui stai creando la stima." : "Compila i contatti: ti chiameremo entro 24h con la valutazione completa."}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Nome *</label>
          <input type="text" value={data.contatto.nome} onChange={e => upd("contatto.nome", e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-lg" data-testid="stima-nome" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Cognome *</label>
          <input type="text" value={data.contatto.cognome} onChange={e => upd("contatto.cognome", e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-lg" data-testid="stima-cognome" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Email *</label>
          <input type="email" value={data.contatto.email} onChange={e => upd("contatto.email", e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-lg" data-testid="stima-email" />
        </div>
        <div>
          <label className="text-xs text-zinc-600 mb-1 block">Telefono *</label>
          <input type="tel" value={data.contatto.telefono} onChange={e => upd("contatto.telefono", e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded-lg" data-testid="stima-telefono" />
        </div>
      </div>
      <div className="mt-6 space-y-2">
        <Checkbox label={<span>Ho letto e accetto la <a href="/privacy-policy" target="_blank" rel="noopener" className="underline text-[#163321]">Privacy Policy</a> *</span>} checked={data.contatto.consenso_privacy} onChange={v => upd("contatto.consenso_privacy", v)} testid="stima-consenso-privacy" />
        <Checkbox label="Acconsento al trattamento per finalità commerciali (newsletter, offerte)" checked={data.contatto.consenso_marketing} onChange={v => upd("contatto.consenso_marketing", v)} />
      </div>
    </div>
  );
}

function Checkbox({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="mt-0.5" data-testid={testid} />
      <span className="text-sm text-zinc-700">{label}</span>
    </label>
  );
}

// ===========================================================================
// Admin post-submit view
// ===========================================================================
function AdminResultView({ result, onReset }) {
  const c = result?.calcolo || {};
  const fmt = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
  const review = c.needs_review_voci || [];
  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl text-[#163321]">Stima creata</h2>
        <button onClick={onReset} className="text-sm text-zinc-600 underline">Nuova stima</button>
      </div>
      {review.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div><strong>Stima provvisoria</strong> — {review.length} parametri marcati needs_review</div>
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <div className="p-4 border rounded-xl">
          <div className="text-xs text-zinc-500">Min</div>
          <div className="font-bold text-zinc-700 text-lg">{fmt(c.valore_per_polizza_minimo)}</div>
        </div>
        <div className="p-4 border-2 border-[#163321] rounded-xl bg-[#163321]/5">
          <div className="text-xs text-[#163321]">Polizza consigliata</div>
          <div className="font-bold text-[#163321] text-2xl">{fmt(c.valore_per_polizza_consigliato)}</div>
        </div>
        <div className="p-4 border rounded-xl">
          <div className="text-xs text-zinc-500">Max</div>
          <div className="font-bold text-zinc-700 text-lg">{fmt(c.valore_per_polizza_massimo)}</div>
        </div>
      </div>
      <div className="text-sm space-y-1">
        <div>Tipologia: <strong>{c.tipologia}</strong> · Superficie: <strong>{c.superficie_mq} mq</strong></div>
        <div>Costo netto medio: <strong>{fmt(c.costo_totale_netto_medio)}</strong> · Lordo: <strong>{fmt(c.costo_totale_lordo_medio)}</strong></div>
        <div>Moltiplicatore totale: <strong>{c.moltiplicatore_totale}</strong></div>
        <div>Coefficienti applicati: <strong>{(c.coefficienti_applicati || []).length}</strong></div>
      </div>
    </div>
  );
}
